// Shared presentational primitives. Class names come straight from the mockup CSS.
import React from "react";
import Icon from "./Icon";
import { grad, hueFor } from "../lib/gradients";
import useImageFallback from "../lib/useImageFallback";
import { getClubInitials } from "../../../utils/clubInitials";

const cx = (...a) => a.filter(Boolean).join(" ");

/** Round avatar: photo url if given, else initials on a stable gradient. `id` seeds the hue. */
export function Avatar({ name, url, id, hue, size = 40, className = "", style }) {
  const h = hue ?? hueFor(id ?? name);
  const base = { "--a": `${size}px`, ...style };
  const img = useImageFallback(url);
  if (!img.failed) {
    return <img className={cx("avatar", className)} src={img.src} onError={img.onError} alt="" style={{ ...base, objectFit: "cover" }} />;
  }
  return (
    <span className={cx("avatar", className)} style={{ ...base, background: grad(h) }} aria-hidden="true">
      {getClubInitials(name)}
    </span>
  );
}

/** Club logo: `club.logo_url` if present, else initials on the club's stable gradient. */
export function ClubLogo({ club, size = 48, className = "", style }) {
  const base = { "--a": `${size}px`, ...style };
  const img = useImageFallback(club?.logo_url);
  if (!img.failed) {
    return <img className={cx("logo", className)} src={img.src} onError={img.onError} alt="" style={{ ...base, objectFit: "cover" }} />;
  }
  return (
    <span className={cx("logo", className)} style={{ ...base, background: grad(hueFor(club?.id ?? club?.name)) }} aria-hidden="true">
      {getClubInitials(club?.name)}
    </span>
  );
}

/** Status pill. tone: blue | green | amber | red | purple | gray | live. */
export function Pill({ tone = "blue", icon, className = "", children, ...rest }) {
  return (
    <span className={cx("pill", `p-${tone}`, className)} {...rest}>
      {icon && <Icon name={icon} size={13} />}
      {children}
    </span>
  );
}

/** Button. variant: pri | sec | ghost | ok | danger | dangerSolid. size: "" | sm | xs. */
export function Button({ variant = "pri", size = "", full = false, icon, className = "", type = "button", children, ...rest }) {
  return (
    <button type={type} className={cx("btn", variant, size, full && "full", className)} {...rest}>
      {icon && <Icon name={icon} size={size ? 14 : 16} />}
      {children}
    </button>
  );
}

/** Header-band chip (white pill). `glass` for the translucent style, `iconOnly` for the round 38px one, `dot` for an unread dot. */
export const Chip = React.forwardRef(function Chip({ glass = false, iconOnly = false, dot = false, icon, className = "", children, as: Tag = "button", ...rest }, ref) {
  const props = Tag === "button" ? { type: "button", ...rest } : rest;
  return (
    <Tag ref={ref} className={cx("chip", glass && "glass", iconOnly && "icon", className)} {...props}>
      {icon && <Icon name={icon} size={iconOnly ? 18 : 16} />}
      {children}
      {dot && <span className="dot" />}
    </Tag>
  );
});

/** Filter chip (`.fchip`); `on` = selected. */
export function FilterChip({ on = false, className = "", children, ...rest }) {
  return <button type="button" className={cx("fchip", on && "on", className)} aria-pressed={on} {...rest}>{children}</button>;
}

/** Round icon-only button; `label` is required for accessibility. */
export function IconButton({ icon, label, size = 18, className = "", ...rest }) {
  return (
    <button type="button" className={cx("ibtn", className)} aria-label={label} {...rest}>
      <Icon name={icon} size={size} />
    </button>
  );
}

/**
 * Labeled form field. `as`: "input" (default) | "select" | "textarea".
 * For select pass <option> children. Extra props go to the control.
 */
export function Field({ label, as = "input", hint, error, className = "", children, ...rest }) {
  const Control = as;
  return (
    <label className={cx("field", className)}>
      {label && <span>{label}</span>}
      <Control className="input" {...rest}>{as === "select" ? children : undefined}</Control>
      {hint && !error && <span className="meta" style={{ fontWeight: 400 }}>{hint}</span>}
      {error && <span className="err-text">{error}</span>}
    </label>
  );
}

/**
 * Segmented control. options: [{ value, label, count?, testId? }]. Controlled via value/onChange(value).
 * `testId` names the group; each tab gets the option's `testId`, else `${testId}.${value}`.
 */
export function Seg({ options, value, onChange, label, className = "", testId }) {
  return (
    <div className={cx("seg", className)} role="tablist" aria-label={label} data-testid={testId}>
      {options.map((o) => (
        <button key={o.value} type="button" role="tab" data-testid={o.testId ?? (testId ? `${testId}.${o.value}` : undefined)} aria-selected={value === o.value} className={value === o.value ? "on" : ""} onClick={() => onChange?.(o.value)}>
          {o.label}
          {o.count != null && <span className="n tnum">{o.count}</span>}
        </button>
      ))}
    </div>
  );
}

/** White rounded card. `pad` adds the 18px padding. */
export function Card({ pad = false, className = "", as: Tag = "div", ...rest }) {
  return <Tag className={cx("card", pad && "pad", className)} {...rest} />;
}

/** Empty / message state inside a card. */
export function Empty({ icon, title, children, className = "" }) {
  return (
    <div className={cx("card empty", className)}>
      {icon && <Icon name={icon} size={28} style={{ color: "var(--g400)" }} />}
      {title && <h3 style={{ fontSize: 17, fontWeight: 700, color: "var(--ink)", marginTop: icon ? 8 : 0 }}>{title}</h3>}
      {children && <div style={{ marginTop: title ? 4 : 0 }}>{children}</div>}
    </div>
  );
}

/** Loading spinner. `large` for page-level. */
export function Spinner({ large = false, label = "Loading" }) {
  return <span className={cx("spinner", large && "lg")} role="status" aria-label={label} />;
}

/** Full-area centered spinner for route-level loading. */
export function PageSpinner() {
  return <div className="center-fill"><Spinner large /></div>;
}

/** Skeleton list rows (avatar circle + two lines). */
export function SkeletonRows({ rows = 4 }) {
  return (
    <div className="card" aria-busy="true">
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="skel-row">
          <span className="skel circle" />
          <div className="grow col" style={{ gap: 8 }}>
            <span className="skel line" style={{ width: "55%" }} />
            <span className="skel line" style={{ width: "35%" }} />
          </div>
        </div>
      ))}
    </div>
  );
}

/** Page scroll area + `.content` wrapper that sits under a Band. `flat` = no overlap with the band. */
export function Content({ flat = false, className = "", style, children }) {
  return <div className={cx("content", flat && "flat", className)} style={style}>{children}</div>;
}
